import { CommonModule } from '@angular/common';
import { Component, Injector, Input, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { BehaviorSubject, Observable, first, take, takeUntil } from 'rxjs';
import { WhiteLabelChartFilterActions } from 'src/app/core/common';
import { WhiteLabelTotalSessions } from 'src/app/core/data/model';
import { BlocState, InitEvent } from 'src/app/shared/bloc/base-bloc';
import { BaseComponent } from 'src/app/shared/component/base-component/base.component';
import { DateRangeForm } from 'src/app/shared/component/date-range/date-range.component';
import { LayoutLoadingComponent } from 'src/app/shared/component/layout-loading/layout-loading.component';
import { StatisticsFilterForm, TableFilter } from 'src/app/shared/interfaces';
import { LineChart } from 'src/app/shared/model';

import { StatisticsChartBloc, StatisticsChartReadyStateState } from '../../bloc/statistics-chart-bloc';
import { StatisticsChartFilterComponent } from '../statistics-chart-filter/statistics-chart-filter.component';
import { StatisticsChartComponent } from '../statistics-chart/statistics-chart.component';
import { ColorsConstants } from './../../../../../shared/common/colors-constants';

@Component({
  selector: 'app-statistics-chart-section',
  standalone: true,
  imports: [CommonModule, LayoutLoadingComponent, StatisticsChartFilterComponent, StatisticsChartComponent],
  templateUrl: './statistics-chart-section.component.html',
  styleUrls: ['./statistics-chart-section.component.scss'],
  providers: [StatisticsChartBloc],
})
export class StatisticsChartSectionComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() cardsData: WhiteLabelTotalSessions;
  @Input() filters: BehaviorSubject<StatisticsFilterForm> = new BehaviorSubject<StatisticsFilterForm>(null);
  chartData: LineChart[] = [];
  labels: string[] = [];
  selectedFilterTab: WhiteLabelChartFilterActions;

  // privates
  private filters$: Observable<StatisticsFilterForm>;

  // setters
  set filterObservable(filter: BehaviorSubject<StatisticsFilterForm>) {
    this.filters$ = filter.asObservable();
  }

  get filterValue(): StatisticsFilterForm {
    return this.filters.value;
  }
  // constants
  ColorsConstants = ColorsConstants;

  constructor(injector: Injector, private bloc: StatisticsChartBloc) {
    super(['GENDER'], injector);
    this.bloc.onState.pipe(takeUntil(this.destroy$)).subscribe((state) => this.onHandleBlocState(state));
  }

  //hooks
  ngOnInit(): void {
    this.getChartDataWhenFilterChangeFromOutSide();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['filters']) {
      this.filterObservable = changes['filters'].currentValue;
    }
  }

  // publics
  onStatisticsFilterChanged(filter: TableFilter<{ selectedFilter: WhiteLabelChartFilterActions }>): void {
    this.selectedFilterTab = filter.filter.selectedFilter;
    this.bloc.setEvent.next(
      new InitEvent({
        date: this.filterValue.date,
        categories: this.filterValue.categories,
        type: this.selectedFilterTab,
      })
    );
  }

  // overrides
  override onHandleBlocState(state: BlocState): void {
    super.onHandleBlocState(state);
    if (state instanceof StatisticsChartReadyStateState) {
      this.buildChart(state);
    }
  }

  // privates
  private buildChart(state: StatisticsChartReadyStateState): void {
    this.labels = state.labels;
    this.chartData = [
      new LineChart(
        state.maleDataSets,
        this.translationsList['GENDER']['MALE'],
        ColorsConstants.TRANSPARENT,
        ColorsConstants.MALE_COLOR,
        ColorsConstants.AZURE_COLOR,
        ColorsConstants.MALE_COLOR,
        ColorsConstants.AZURE_COLOR
      ),
      new LineChart(
        state.femalesDataSet,
        this.translationsList['GENDER']['FEMALE'],
        ColorsConstants.TRANSPARENT,
        ColorsConstants.FEMALE_COLOR,
        ColorsConstants.LIGHT_COLOR,
        ColorsConstants.FEMALE_COLOR,
        ColorsConstants.WARN_RED_COLOR
      ),
    ];
  }

  private getChartDataWhenFilterChangeFromOutSide(): void {
    this.filters$.pipe(takeUntil(this.destroy$)).subscribe((filterData) => {
      if (filterData && this.selectedFilterTab) {
        this.bloc.setEvent.next(
          new InitEvent({ date: filterData.date, categories: filterData.categories, type: this.selectedFilterTab })
        );
      }
    });
  }
}
